import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fetchPurchaseActivity,
  purchaseActivityEventKey,
  subscribePurchaseActivity,
} from '@/services/store/purchaseActivity'
import type { PurchaseActivity } from '@/types'

/** Wait before showing the seeded/backfill toast on first visit. */
const INITIAL_DELAY_MS = 10_000
export const PURCHASE_ACTIVITY_DISPLAY_MS = 6_000
const DISPLAY_MS = PURCHASE_ACTIVITY_DISPLAY_MS
/** Fast poll so we catch updates even if the Firestore listener lags. */
const POLL_INTERVAL_MS = 2_000

type ToastRuntime = {
  armed: boolean
  pending: PurchaseActivity | null
  lastProcessedKey: string | null
  dismissedKey: string | null
  hideTimer: ReturnType<typeof setTimeout> | null
  pollTimer: ReturnType<typeof setInterval> | null
}

function isLivePurchase(
  activity: PurchaseActivity,
  mountedAt: number
): boolean {
  if (activity.sequence != null) {
    return activity.sequence >= mountedAt - 2_000
  }
  const purchasedAt = new Date(activity.purchasedAt).getTime()
  return Number.isFinite(purchasedAt) && purchasedAt >= mountedAt - 5_000
}

export function usePurchaseActivityToast() {
  const [visible, setVisible] = useState(false)
  const [activity, setActivity] = useState<PurchaseActivity | null>(null)
  const activityRef = useRef<PurchaseActivity | null>(null)
  const mountedAtRef = useRef(Date.now())
  const runtimeRef = useRef<ToastRuntime>({
    armed: false,
    pending: null,
    lastProcessedKey: null,
    dismissedKey: null,
    hideTimer: null,
    pollTimer: null,
  })

  const clearHideTimer = useCallback(() => {
    const runtime = runtimeRef.current
    if (runtime.hideTimer) {
      clearTimeout(runtime.hideTimer)
      runtime.hideTimer = null
    }
  }, [])

  const dismiss = useCallback(() => {
    const runtime = runtimeRef.current
    clearHideTimer()
    runtime.dismissedKey =
      activityRef.current != null
        ? purchaseActivityEventKey(activityRef.current)
        : runtime.lastProcessedKey
    setVisible(false)
  }, [clearHideTimer])

  useEffect(() => {
    const runtime = runtimeRef.current
    const mountedAt = mountedAtRef.current

    const presentActivity = (next: PurchaseActivity) => {
      const eventKey = purchaseActivityEventKey(next)

      if (runtime.dismissedKey === eventKey) {
        return
      }

      if (runtime.dismissedKey && runtime.dismissedKey !== eventKey) {
        runtime.dismissedKey = null
      }

      if (runtime.lastProcessedKey === eventKey) {
        return
      }

      runtime.lastProcessedKey = eventKey
      activityRef.current = next
      setActivity(next)
      setVisible(true)
      clearHideTimer()
      runtime.hideTimer = setTimeout(() => {
        setVisible(false)
        runtime.hideTimer = null
      }, DISPLAY_MS)
    }

    const ingestActivity = (next: PurchaseActivity | null) => {
      if (!next) return

      const live = isLivePurchase(next, mountedAt)

      if (!runtime.armed && !live) {
        runtime.pending = next
        return
      }

      presentActivity(next)
    }

    const arm = () => {
      runtime.armed = true
      if (runtime.pending) {
        presentActivity(runtime.pending)
        runtime.pending = null
      }
    }

    void fetchPurchaseActivity().then(ingestActivity)

    runtime.pollTimer = setInterval(() => {
      void fetchPurchaseActivity().then(ingestActivity)
    }, POLL_INTERVAL_MS)

    const armTimer = window.setTimeout(arm, INITIAL_DELAY_MS)
    const unsubscribe = subscribePurchaseActivity(ingestActivity)

    return () => {
      window.clearTimeout(armTimer)
      if (runtime.pollTimer) {
        clearInterval(runtime.pollTimer)
        runtime.pollTimer = null
      }
      clearHideTimer()
      unsubscribe()
    }
  }, [clearHideTimer])

  return { visible, activity, dismiss }
}
